import { Logger, TLogger } from 'components/logger/Logger';
import { StateManager } from 'components/stateManager/StateManager';
import { IVideoControllerComponentsCommonProps } from 'components/videoElementController/IVideoElementController';
import {
  EVideoElementControllerBusEvent,
  TVideoControllerEventBus,
} from 'components/videoElementController/VideoElementController.events';
import { IPlayerState } from 'types/common';
import { bind } from 'utils/decorators/bind';
import { addMediaAction } from 'utils/mediaAction';

export class PlaybackController {
  private _logger: TLogger;
  private _eventsMap = {
    [EVideoElementControllerBusEvent.VIDEO_START]: this._startVideo,
    [EVideoElementControllerBusEvent.VIDEO_PAUSE]: this._pauseVideo,
    [EVideoElementControllerBusEvent.VIDEO_RESET]: this._resetVideo,
    [EVideoElementControllerBusEvent.RESTART]: this._restart,
    [EVideoElementControllerBusEvent.DESTROY]: this._destroy,
  };

  private readonly _state: StateManager<IPlayerState>;
  private readonly _eventBus: TVideoControllerEventBus;

  constructor(props: IVideoControllerComponentsCommonProps) {
    this._state = props.state;
    this._eventBus = props.eventBus;
    this._logger = Logger(this._eventBus, 'VideoElementController - PlaybackController');

    this._logger.info('Created');
    this._logger.debug('Initial state', this._state);
    this.init();
  }

  @bind
  public init() {
    this._logger.info('Initialization requested, attaching eventBus listeners');
    this._eventBus.toggleSubscriptionBulk({
      action: 'subscribe',
      callbacks: this._eventsMap,
    });
  }

  @bind
  private _startVideo() {
    const videoElement = this._state.getState().videoElement;
    this._logger.verbose('Starting the video');

    if (videoElement) {
      if (!videoElement.playbackRate) {
        videoElement.playbackRate = 1;
      }

      this._logger.debug('Play call is added to the promises queue');
      return addMediaAction(videoElement, 'play')
        .then(() => {
          this._logger.debug('Play call resolved');
          if (this._state.getState().canPlay) {
            this._eventBus.publish(EVideoElementControllerBusEvent.PLAYING, {});
          }
        })
        .catch((e: Error) => {
          this._state.setState({ isAutoplayError: true });
          this._logger.error('An error occured when trying to play the video.', { error: e });
          if (this._state.getState().canPlay) {
            this._eventBus.publish(EVideoElementControllerBusEvent.PAUSED, {});
          }
        });
    }
  }

  @bind
  private _pauseVideo() {
    this._logger.debug('Pausing the video');
    const videoElement = this._state.getState().videoElement;

    if (videoElement) {
      this._logger.debug('Pause call is added to the promises queue');
      return addMediaAction(videoElement, 'pause')
        .then(() => {
          this._logger.debug('Pause call resolved');
        })
        .catch((e) => {
          this._logger.warn('Could not pause video', e);
        });
    }
  }

  @bind
  private _loadVideo() {
    this._logger.verbose('Loading the video');
    const videoElement = this._state.getState().videoElement;

    if (videoElement?.src && videoElement.src.startsWith('blob')) {
      this._logger.debug('Stream seems to be blob. Skipping load');
      return;
    }

    if (videoElement) {
      this._logger.debug('Load call is added to the promises queue');
      return addMediaAction(videoElement, 'load')
        .then(() => {
          this._logger.debug('Load call resolved');
        })
        .catch((e) => {
          this._logger.warn('Could not load video', e);
        });
    }
  }

  @bind
  private _resetVideo() {
    this._logger.verbose('Resetting the video');
    const videoElement = this._state.getState().videoElement;

    if (videoElement) {
      this._eventBus.publish(EVideoElementControllerBusEvent.LOADING, {});
      this._loadVideo();
    }
  }

  @bind
  private async _restart() {
    this._logger.verbose('Restarting the video');

    await this._pauseVideo();
    // discuss questionable
    this._startVideo();
  }

  @bind
  private _destroy() {
    this._logger.info('Destroying');
    this._eventBus.toggleSubscriptionBulk({
      action: 'unsubscribe',
      callbacks: this._eventsMap,
    });
  }
}
